//frontend/dispatcher-client/src/hooks/usePersonnelRequestsData.js
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  fetchPersonnelPeople,
  fetchPersonnelAbsences,
} from "../api/personnelApi";

import {
  loadPersonnelResource,
  readPersonnelCache,
  subscribePersonnelCache,
} from "../utils/personnelCache";

/* =========================================================
 * CACHE
 * ======================================================= */

const readCurrentCache =
  () => ({
    people:
      readPersonnelCache(
        "people",
        {}
      ),

    absences:
      readPersonnelCache(
        "absenceRequests",
        {}
      ),
  });

/* =========================================================
 * HOOK
 * ======================================================= */

export default function usePersonnelRequestsData() {
  const initial =
    readCurrentCache();

  const [
    people,
    setPeople,
  ] =
    useState(
      () =>
        initial.people
          ?.data ||
        []
    );

  const [
    absences,
    setAbsences,
  ] =
    useState(
      () =>
        initial.absences
          ?.data ||
        []
    );

  const [
    loading,
    setLoading,
  ] =
    useState(
      () =>
        !(
          initial.people &&
          initial.absences
        )
    );

  const [
    refreshing,
    setRefreshing,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState(
      null
    );

  const requestRef =
    useRef(0);

  const realtimeRefreshTimerRef =
    useRef(
      null
    );

  /* =======================================================
   * REFRESH
   * ===================================================== */

  const refresh =
    useCallback(
      async ({
        silent =
          true,
      } = {}) => {
        const requestId =
          requestRef.current +
          1;

        requestRef.current =
          requestId;

        if (!silent) {
          setLoading(
            true
          );
        }

        setRefreshing(
          true
        );

        setError(
          null
        );

        const [
          peopleResult,
          absencesResult,
        ] =
          await Promise.allSettled([
            loadPersonnelResource({
              resource:
                "people",

              params: {},

              force:
                true,

              loader:
                () =>
                  fetchPersonnelPeople(),
            }),

            loadPersonnelResource({
              resource:
                "absenceRequests",

              params: {},

              force:
                true,

              loader:
                () =>
                  fetchPersonnelAbsences(),
            }),
          ]);

        if (
          requestRef.current !==
          requestId
        ) {
          return;
        }

        let failures =
          0;

        if (
          peopleResult.status ===
          "fulfilled"
        ) {
          setPeople(
            peopleResult.value
          );
        } else {
          failures += 1;

          console.warn(
            "[PersonnelRequests] people refresh failed:",
            peopleResult.reason
          );
        }

        if (
          absencesResult.status ===
          "fulfilled"
        ) {
          setAbsences(
            absencesResult.value
          );
        } else {
          failures += 1;

          console.warn(
            "[PersonnelRequests] absences refresh failed:",
            absencesResult.reason
          );
        }

        if (
          failures > 0
        ) {
          setError(
            new Error(
              "Some Personnel request data could not be refreshed."
            )
          );
        }

        setLoading(
          false
        );

        setRefreshing(
          false
        );
      },
      []
    );

  /* =======================================================
   * INITIAL LOAD
   * ===================================================== */

  useEffect(
    () => {
      const cached =
        readCurrentCache();

      if (
        cached.people
      ) {
        setPeople(
          cached.people.data
        );
      }

      if (
        cached.absences
      ) {
        setAbsences(
          cached.absences.data
        );
      }

      const hasCache =
        !!(
          cached.people &&
          cached.absences
        );

      const hasStaleCache =
        Boolean(
          cached.people
            ?.isStale ||
          cached.absences
            ?.isStale
        );

      setLoading(
        !hasCache
      );

      if (
        !hasCache ||
        hasStaleCache
      ) {
        refresh({
          silent:
            hasCache,
        });
      } else {
        setRefreshing(
          false
        );

        setError(
          null
        );
      }
    },
    [
      refresh,
    ]
  );

  /* =======================================================
   * CACHE SYNC + REALTIME
   * ===================================================== */

  useEffect(
    () => {
      const relevantResources =
        new Set([
          "people",
          "absenceRequests",
        ]);

      const unsubscribe =
        subscribePersonnelCache(
          (
            event
          ) => {
            if (
              !relevantResources.has(
                event.resource
              )
            ) {
              return;
            }

            /*
             * Realtime mutations mark the resource stale.
             *
             * Coalesce events arriving almost together
             * into one silent refresh.
             */
            if (
              event.stale ===
              true
            ) {
              const requestVersionAtEvent =
                requestRef.current;

              if (
                realtimeRefreshTimerRef.current
              ) {
                window.clearTimeout(
                  realtimeRefreshTimerRef.current
                );
              }

              realtimeRefreshTimerRef.current =
                window.setTimeout(
                  () => {
                    realtimeRefreshTimerRef.current =
                      null;

                    /*
                     * If another refresh started after
                     * this event, it is already handling
                     * the newest data.
                     */
                    if (
                      requestRef.current !==
                      requestVersionAtEvent
                    ) {
                      return;
                    }

                    refresh({
                      silent:
                        true,
                    }).catch(
                      () => {
                        /*
                         * refresh() owns the error state.
                         */
                      }
                    );
                  },
                  60
                );

              return;
            }

            /*
             * Normal cache writes are consumed
             * immediately without another request.
             */
            const cached =
              readCurrentCache();

            if (
              cached.people
            ) {
              setPeople(
                cached.people.data
              );
            }

            if (
              cached.absences
            ) {
              setAbsences(
                cached.absences.data
              );
            }
          }
        );

      return () => {
        unsubscribe();

        if (
          realtimeRefreshTimerRef.current
        ) {
          window.clearTimeout(
            realtimeRefreshTimerRef.current
          );

          realtimeRefreshTimerRef.current =
            null;
        }
      };
    },
    [
      refresh,
    ]
  );

  return {
    people,
    absences,

    loading,
    refreshing,
    error,

    refresh,
  };
}